import {
  isReservedScheduleType,
  ReservedScheduleTypeEnum,
} from "@/lib/enums/reserved-schedule-type";

/**
 * `CodTipoTermoDeUso` do backend — `livance-system`,
 * `Livance.InfraEstruturaCore/Models/Enums/CodTipoTermoDeUso.cs`.
 *
 * `AdesaoHorariosMenores = 5` está **no meio da sequência** e não é termo de Período Garantido —
 * é ele que desalinha modalidade e tipo de termo a partir da Careplus. Está listado aqui
 * justamente para o desalinhamento ficar visível a quem ler o enum, não só a quem ler o mapa
 * abaixo.
 */
export enum TermsOfUseTypeEnum {
  PeriodoGarantido = 1,
  PeriodoGarantidoPorto = 2,
  PeriodoGarantidoPsico = 3,
  PeriodoGarantidoParcerias = 4,
  HorariosMenores = 5,
  PeriodoGarantidoCareplus = 6,
  PeriodoGarantidoSami = 7,
  PeriodoGarantidoSaludia = 8,
}

/**
 * Modalidade de Período Garantido → tipo de termo de uso que ela exige.
 *
 * **Espelho do mapa canônico do backend**, que é um `switch` em
 * `Livance.Negocio/Negocios/UsuariosTermosDeUsos/UsuarioTermoDeUsoNegocio.cs`
 * (`SelecionaTermoAdesaoPeriodoGarantidoPorUsuarioEModalidadeAsync`). Os pares são os mesmos, par
 * por par — se um dia divergirem, o backend é quem está certo.
 *
 * Os quatro primeiros pares coincidem numericamente, e **é só coincidência**: a partir da Careplus
 * os valores divergem em um, porque `HorariosMenores = 5` ocupa uma posição no meio de
 * `CodTipoTermoDeUso` sem ter modalidade correspondente.
 *
 * Enquanto a escolha de modalidade era implícita, o desalinhamento era latente — o mapa cobria
 * `1`, `2` e `4` e o resto caía na identidade, pedindo um termo plausível e errado. Com o campo de
 * categoria, ele vira um caminho em que **o profissional assina o documento de outra parceria**:
 * a Careplus receberia "Adesão Horários Menores" para aceitar.
 *
 * Duas coisas impedem a regressão, e nenhuma delas depende de vigilância humana: o mapa é
 * **total** sobre as sete modalidades, então não existe caminho de fallback para um termo
 * plausível e errado; e o `satisfies` faz de "esqueci uma modalidade" um erro de `typecheck`.
 * Indexar com um `number` não estreitado também não compila — quem tem um valor cru precisa
 * passar por `isReservedScheduleType` antes.
 */
export const TERMS_OF_USE_BY_MODALITY = {
  [ReservedScheduleTypeEnum.Livance]: TermsOfUseTypeEnum.PeriodoGarantido,
  [ReservedScheduleTypeEnum.Porto]: TermsOfUseTypeEnum.PeriodoGarantidoPorto,
  [ReservedScheduleTypeEnum.Psico]: TermsOfUseTypeEnum.PeriodoGarantidoPsico,
  [ReservedScheduleTypeEnum.BizDev]: TermsOfUseTypeEnum.PeriodoGarantidoParcerias,
  [ReservedScheduleTypeEnum.Careplus]: TermsOfUseTypeEnum.PeriodoGarantidoCareplus,
  [ReservedScheduleTypeEnum.Sami]: TermsOfUseTypeEnum.PeriodoGarantidoSami,
  [ReservedScheduleTypeEnum.Saludia]: TermsOfUseTypeEnum.PeriodoGarantidoSaludia,
} satisfies Record<ReservedScheduleTypeEnum, TermsOfUseTypeEnum>;

/**
 * O tipo de termo que a modalidade exige, ou `undefined` quando o número não é uma das sete.
 *
 * Existe para quem tem um `number` **cru** — o valor de um campo de formulário, o de uma flag — e
 * por isso não pode indexar `TERMS_OF_USE_BY_MODALITY` direto. O `undefined` no lugar de um fallback
 * é a mesma decisão do mapa: devolver um termo plausível e errado faria o profissional assinar o
 * documento de outra parceria.
 */
export const findTermsOfUseType = (
  modality: number,
): TermsOfUseTypeEnum | undefined => {
  return isReservedScheduleType(modality)
    ? TERMS_OF_USE_BY_MODALITY[modality]
    : undefined;
};

/**
 * Artigo público do termo de uso de cada modalidade, chaveado por **modalidade**
 * (`codModalidadePeriodoGarantido`), não por tipo de termo.
 *
 * Ao contrário de `TERMS_OF_USE_BY_MODALITY`, este mapa é **parcial de propósito**: Careplus (`5`),
 * Sami (`6`) e Saludia (`7`) não têm artigo publicado. O tipo é `Partial<Record<...>>` para que
 * indexar devolva `string | undefined` e nenhum chamador possa esquecer o caso ausente.
 */
const TERMS_OF_USE_LINK_BY_MODALITY = {
  [ReservedScheduleTypeEnum.Livance]:
    "https://ajuda.livance.com.br/pt-BR/articles/8011141-termos-de-uso-periodo-garantido",
  [ReservedScheduleTypeEnum.Porto]:
    "https://ajuda.livance.com.br/pt-BR/articles/8075573-termos-de-uso-periodo-garantido-porto-seguro",
  [ReservedScheduleTypeEnum.BizDev]:
    "https://ajuda.livance.com.br/pt-BR/articles/8260193-termos-de-uso-periodo-garantido-parcerias",
} satisfies Partial<Record<ReservedScheduleTypeEnum, string>>;

/**
 * A modalidade tem artigo de termo de uso publicado?
 *
 * **É um gate de oferta, não só de renderização.** Onde o link só informa — o resumo de um PG já
 * criado, a tela de cancelamento — a ausência dele é aceitável e o texto vira `span` em vez de
 * âncora. No **drawer de aceite** do campo de categoria não é: pedir a assinatura de um documento
 * que o profissional não consegue abrir é pior do que não oferecer a categoria. Então a modalidade
 * sem link fica **fora** do campo (KRA-516, decisão de 19/08/2026).
 *
 * O critério é deliberadamente a presença da URL, e não uma lista de exclusão à parte: no dia em
 * que o artigo for publicado, acrescentar a entrada no mapa acima libera a categoria — sem que
 * ninguém precise lembrar de mexer num segundo lugar.
 *
 * Isso **não** vale para a Psico. A exclusão dela é permanente e vive em
 * `DISCONTINUED_RESERVED_SCHEDULE_TYPES`, separada e explícita: descontinuada é regra de produto,
 * falta de link é circunstância que um dia deixa de valer.
 */
export const hasPublishedTermsOfUseLink = (modality: number): boolean => {
  return findTermsOfUseLink(modality) !== undefined;
};

/**
 * O artigo do termo de uso da modalidade, ou `undefined` quando não há.
 *
 * Aceita `null`/`undefined` porque os sítios de cancelamento leem
 * `nextSchedule?.codModalidadePeriodoGarantido`, que pode não ter chegado ainda.
 */
export const findTermsOfUseLink = (
  modality: number | null | undefined,
): string | undefined => {
  if (modality == null || !(modality in TERMS_OF_USE_LINK_BY_MODALITY)) {
    return undefined;
  }

  return TERMS_OF_USE_LINK_BY_MODALITY[
    modality as keyof typeof TERMS_OF_USE_LINK_BY_MODALITY
  ];
};
